import { cn } from "@gigpro/ui/tailwind/cn";
import { Star } from "lucide-react";
import * as React from "react";
import { times } from "remeda";

export interface RatingProps extends React.ComponentProps<"span"> {
  value: number | null | undefined;
  onValueChange?: (value: number | null) => void;
  /** -1 when not hovering */
  onHoverChange?: (value: number) => void;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  readOnly?: boolean;
}

const MAX_RATING = 5;

const sizeClasses = {
  small: "size-3.5",
  medium: "size-4",
  large: "size-5",
} as const;

function getLabelText(value: number | null | undefined) {
  if (value == null || value === 0) return "No rating";
  return `${value} Star${value === 1 ? "" : "s"}`;
}

export const Rating = ({
  ref,
  value,
  onValueChange,
  onHoverChange,
  size = "medium",
  disabled = false,
  readOnly = false,
  className,
  onMouseMove,
  onMouseLeave,
  ...props
}: RatingProps) => {
  const name = React.useId();

  const [preview, setPreview] = React.useState({ hover: -1, focus: -1 });
  const { hover, focus } = preview;
  const [focusVisible, setFocusVisible] = React.useState(false);
  const rootRef = React.useRef<HTMLSpanElement>(null);

  React.useImperativeHandle(ref, () => rootRef.current as HTMLSpanElement);

  const isInteractive = !disabled && !readOnly && Boolean(onValueChange);

  const [prevInteractive, setPrevInteractive] = React.useState(isInteractive);
  if (prevInteractive !== isInteractive) {
    setPrevInteractive(isInteractive);
    if (!isInteractive) {
      setPreview({ hover: -1, focus: -1 });
      setFocusVisible(false);
    }
  }

  // Display value: hover takes priority, then focus, then actual value
  let displayValue = value ?? 0;
  if (hover !== -1) displayValue = hover;
  else if (focus !== -1) displayValue = focus;

  // Calculate which star the cursor is over based on position within the component
  const handleMouseMove = (event: React.MouseEvent<HTMLSpanElement>) => {
    onMouseMove?.(event);
    if (!isInteractive) return;

    const root = rootRef.current;
    if (!root) return;

    const { left, width } = root.getBoundingClientRect();
    const percent = (event.clientX - left) / width;
    const newHover = Math.max(1, Math.min(MAX_RATING, Math.ceil(percent * MAX_RATING)));

    setPreview((prev) => (prev.hover === newHover ? prev : { hover: newHover, focus: newHover }));
    setFocusVisible(false);

    if (hover !== newHover) {
      onHoverChange?.(newHover);
    }
  };

  // Reset hover state when cursor leaves the component
  const handleMouseLeave = (event: React.MouseEvent<HTMLSpanElement>) => {
    onMouseLeave?.(event);
    if (!isInteractive) return;

    setPreview({ hover: -1, focus: -1 });
    if (hover !== -1) {
      onHoverChange?.(-1);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value === "" ? null : Number.parseFloat(event.target.value);
    // Give mouse priority over keyboard
    if (hover !== -1) newValue = hover;

    onValueChange?.(newValue);
  };

  const handleClear = (event: React.MouseEvent<HTMLInputElement>) => {
    // Ignore keyboard-triggered clicks
    if (event.clientX === 0 && event.clientY === 0) return;

    if (Number.parseFloat((event.target as HTMLInputElement).value) === value) {
      onValueChange?.(null);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Check if focus came from keyboard (not mouse)
    if (event.target.matches(":focus-visible")) {
      setFocusVisible(true);
    }
    const newFocus = Number.parseFloat(event.target.value);
    setPreview((prev) => ({ hover: prev.hover, focus: newFocus }));
  };

  const handleBlur = () => {
    if (hover !== -1) return;
    setFocusVisible(false);
    setPreview((prev) => ({ hover: prev.hover, focus: -1 }));
  };

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- focusable image region needs keyboard access
    <span
      ref={rootRef}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- native element cannot carry this markup
      role="img"
      aria-label={getLabelText(value)}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
      data-interactive={isInteractive || undefined}
      data-focus-visible={focusVisible || undefined}
      className={cn(
        "relative inline-flex w-fit gap-0.5 text-warning",
        "data-interactive:cursor-pointer",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "data-readonly:pointer-events-none",
        "data-focus-visible:**:data-active:outline data-focus-visible:**:data-active:outline-gray",
        className,
      )}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {times(MAX_RATING, (index) => {
        const itemValue = index + 1;
        const isFilled = itemValue <= displayValue;
        const isActive = itemValue === Math.ceil(displayValue) && (hover !== -1 || focus !== -1);
        const isChecked = itemValue === value;

        const iconElement = (
          <span
            data-active={isActive || undefined}
            data-filled={isFilled || undefined}
            className="group pointer-events-none flex transition-transform duration-150 data-active:scale-[1.2]"
          >
            <Star
              // mui test ids to keep backwards compatibility
              data-testid={isFilled ? "StarIcon" : "StarBorderIcon"}
              className={cn(
                sizeClasses[size],
                "fill-none stroke-light-gray text-light-gray",
                "group-data-filled:fill-current group-data-filled:stroke-current group-data-filled:text-current",
              )}
            />
          </span>
        );

        if (readOnly) {
          return (
            <span key={itemValue} data-filled={isFilled || undefined}>
              {iconElement}
            </span>
          );
        }

        return (
          <React.Fragment key={itemValue}>
            <label htmlFor={`${name}-${itemValue}`} className="cursor-[inherit]">
              {iconElement}
              <span className="sr-only">{getLabelText(itemValue)}</span>
            </label>
            <input
              className="sr-only"
              type="radio"
              name={name}
              id={`${name}-${itemValue}`}
              value={itemValue}
              checked={isChecked}
              disabled={disabled}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onClick={handleClear}
            />
          </React.Fragment>
        );
      })}
    </span>
  );
};

Rating.displayName = "Rating";
